import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PeopleAltOutlined,
  PhoneInTalkOutlined,
} from '@mui/icons-material';
import { salesLeadService } from '../../services/salesLeadService';
import {
  LEAD_STATUS_META,
  SalesCard,
  SalesEmptyState,
  SalesEntityAvatar,
  SalesPageHeader,
  SalesStatCard,
  SalesStatusChip,
  SALES,
  salesPrimaryButtonSx,
  salesRowHoverSx,
  salesTableHeadSx,
} from '../../components/sales/salesUi';

const SalesLeads = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await salesLeadService.getAll({
        search: search || undefined,
        status: status || undefined,
        limit: 100,
      });
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const all = rows;
    return {
      total: all.length,
      active: all.filter((r) => ['new', 'contacted', 'quoting'].includes(r.status)).length,
      quoting: all.filter((r) => r.status === 'quoting').length,
      converted: all.filter((r) => r.status === 'converted').length,
    };
  }, [rows]);

  return (
    <Box>
      <SalesPageHeader
        title="My Leads"
        subtitle="Capture prospects from the field, track follow-ups, and move opportunities toward a quote."
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => navigate('/sales/leads/new')}
              sx={{ textTransform: 'none', borderColor: SALES.line, color: SALES.ink }}
            >
              New Lead
            </Button>
            <Button
              variant="contained"
              startIcon={<PhoneInTalkOutlined />}
              onClick={() => navigate('/sales/activity/new')}
              sx={salesPrimaryButtonSx}
            >
              Log Call / Visit
            </Button>
          </>
        }
      />

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Pipeline" value={stats.total} hint="Leads in this view" />
        </Grid>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Active" value={stats.active} hint="New · Contacted · Quoting" accent={SALES.info} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Quoting" value={stats.quoting} hint="Ready for quote request" accent={SALES.warning} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SalesStatCard label="Converted" value={stats.converted} hint="Became customers" accent={SALES.success} />
        </Grid>
      </Grid>

      <SalesCard sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            size="small"
            placeholder="Search company, contact, phone, source…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: SALES.muted, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All statuses</MenuItem>
              {Object.entries(LEAD_STATUS_META).map(([key, meta]) => (
                <MenuItem key={key} value={key}>{meta.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </SalesCard>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <SalesEmptyState
          icon={<PeopleAltOutlined sx={{ fontSize: 42 }} />}
          title={search || status ? 'No matching leads' : 'No leads yet'}
          description={
            search || status
              ? 'Try clearing filters or searching a different company name.'
              : 'Log your first call or visit — you can create the lead in the same step.'
          }
          actionLabel={search || status ? undefined : 'Log Call / Visit'}
          onAction={search || status ? undefined : () => navigate('/sales/activity/new')}
        />
      ) : (
        <TableContainer component={SalesCard}>
          <Table size="small">
            <TableHead sx={salesTableHeadSx}>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={salesRowHoverSx}
                  onClick={() => navigate(`/sales/leads/${row.id}`)}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 700, color: SALES.steel }}>
                      #{row.lead_number}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <SalesEntityAvatar name={row.company_name} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: SALES.ink }}>
                          {row.company_name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: SALES.muted }}>
                          {[row.city, row.state].filter(Boolean).join(', ') || 'No location yet'}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>{row.contact_name || '—'}</TableCell>
                  <TableCell>{row.contact_phone || row.phone || '—'}</TableCell>
                  <TableCell>{row.lead_source || '—'}</TableCell>
                  <TableCell>
                    <SalesStatusChip status={row.status} metaMap={LEAD_STATUS_META} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SalesLeads;
